import React, {useEffect, useState} from 'react'
import {Link, useNavigate, useSearchParams} from 'react-router-dom'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useCognitoAuth} from '@/app/modules/auth'
import {getLogger} from '@/shared/utils/logUtils'
import {toAbsoluteUrl} from '@/shared/utils'
import {sharingQueryKeys} from './hooks'
import {INVITATION_RETURN_KEY, SHARED_FOLDER_ICON} from './constants'
import {readShareError, shareErrorMessage} from './errors'
import {accessLabel, formatShareDate} from './format'
import {
  acceptShareInvitation,
  declineShareInvitation,
  previewShareInvitation,
} from './managementApi'
import {shareManagementKeys} from './managementHooks'
import {OwnedBy} from './OwnedBy'
import type {ShareView, ShareInvitationPreview} from './types'

/**
 * Where an invitation can end up. `wrong_account`, `used`, `expired`, `withdrawn` and `unavailable`
 * are the states the emailed link must explain clearly (P1-F5).
 */
type View =
  | {kind: 'signed_out'}
  | {kind: 'no_token'}
  | {kind: 'loading'}
  | {kind: 'ready'; preview: ShareInvitationPreview}
  | {kind: 'answering'; preview: ShareInvitationPreview; answer: 'accept' | 'decline'}
  | {kind: 'accepted'; share: Pick<ShareView, 'name' | 'ownerName' | 'access'>}
  | {kind: 'declined'; shareName: string}
  | {kind: 'already_member'; shareName: string}
  | {kind: 'wrong_account'; invitedEmail: string; message?: string}
  | {kind: 'used'}
  | {kind: 'expired'}
  | {kind: 'withdrawn'}
  | {kind: 'unavailable'}
  | {kind: 'error'; message: string; preview?: ShareInvitationPreview}

const saveReturnUrl = (token: string | null) => {
  if (!token) return
  try {
    sessionStorage.setItem(
      INVITATION_RETURN_KEY,
      `/share-invitation?token=${encodeURIComponent(token)}`
    )
  } catch {
    /* sessionStorage unavailable: the user can open the link again after signing in */
  }
}

/** Same masking as the server's `mask_email`, to tell an unverified address from another one. */
const maskEmail = (email: string | undefined): string => {
  const [local, domain] = (email ?? '').toLowerCase().split('@')
  return domain ? `${local.slice(0, 1)}…@${domain}` : ''
}

/** Views after which the link has nothing more to offer, so sign-in stops returning to it. */
const FINAL_VIEWS: View['kind'][] = [
  'accepted',
  'declined',
  'already_member',
  'used',
  'expired',
  'withdrawn',
  'unavailable',
]

const clearReturnUrl = () => {
  try {
    sessionStorage.removeItem(INVITATION_RETURN_KEY)
  } catch {
    /* nothing to clear */
  }
}

/** The view for a preview whose invitation can no longer be answered, if that is the case. */
function viewForStatus(preview: ShareInvitationPreview): View | null {
  switch (preview.status) {
    case 'accepted':
      return {kind: 'used'}
    case 'expired':
      return {kind: 'expired'}
    case 'revoked':
    case 'declined':
      return {kind: 'withdrawn'}
    default:
      return preview.emailMatches
        ? null
        : {kind: 'wrong_account', invitedEmail: preview.invitedEmail}
  }
}

/** The view for a failed preview, accept or decline (codes from `invitations_api.py`). */
function viewForError(err: unknown, preview?: ShareInvitationPreview): View {
  const {status, code} = readShareError(err)
  if (status === 404 || code === 'not_found') return {kind: 'unavailable'}
  if (code === 'already_used') return {kind: 'used'}
  if (code === 'invitation_expired') return {kind: 'expired'}
  if (code === 'invitation_withdrawn' || status === 410) return {kind: 'withdrawn'}
  if (code === 'email_mismatch') {
    return {
      kind: 'wrong_account',
      invitedEmail: preview?.invitedEmail ?? '',
      message: preview?.invitedEmail ? undefined : shareErrorMessage(err),
    }
  }
  if (code === 'already_member' && preview) {
    return {kind: 'already_member', shareName: preview.shareName}
  }
  return {kind: 'error', message: shareErrorMessage(err), preview}
}

/**
 * `/share-invitation?token=` — the link in a shared-folder invitation email. Signed out, it keeps
 * the token across sign-in and sign-up exactly like the subscription `InvitationPage`; signed in,
 * it shows who shared what, then accepts or declines.
 */
export const ShareInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {currentUser, cognitoLogout} = useCognitoAuth()
  const token = searchParams.get('token')
  const signedIn = !!currentUser?.sub

  const preview = useQuery({
    queryKey: shareManagementKeys.invitationPreview(token ?? ''),
    queryFn: () => previewShareInvitation(token!),
    enabled: signedIn && !!token,
    retry: false,
    staleTime: Infinity,
  })

  const [answerView, setAnswerView] = useState<View | null>(null)

  useEffect(() => {
    document.body.classList.add('signInPage')
    return () => {
      document.body.classList.remove('signInPage')
    }
  }, [])

  const view: View = (() => {
    if (!token) return {kind: 'no_token'}
    if (!signedIn) return {kind: 'signed_out'}
    if (answerView) return answerView
    if (preview.isLoading) return {kind: 'loading'}
    if (preview.isError) return viewForError(preview.error)
    if (!preview.data) return {kind: 'loading'}
    return viewForStatus(preview.data) ?? {kind: 'ready', preview: preview.data}
  })()

  useEffect(() => {
    if (FINAL_VIEWS.includes(view.kind)) clearReturnUrl()
  }, [view.kind])

  const answer = async (choice: 'accept' | 'decline') => {
    if (!token || !preview.data) return
    const current = preview.data
    setAnswerView({kind: 'answering', preview: current, answer: choice})
    try {
      if (choice === 'accept') {
        const share = await acceptShareInvitation(token)
        setAnswerView({kind: 'accepted', share})
      } else {
        await declineShareInvitation(token)
        setAnswerView({kind: 'declined', shareName: current.shareName})
      }
      queryClient.invalidateQueries({queryKey: sharingQueryKeys.shares()})
      queryClient.invalidateQueries({queryKey: shareManagementKeys.pendingInvitations()})
    } catch (err) {
      getLogger().warn(`Share invitation ${choice} failed`, err)
      setAnswerView(viewForError(err, current))
    }
  }

  const switchAccount = () => {
    // Back here after signing in with the invited address.
    saveReturnUrl(token)
    void cognitoLogout()
  }

  return (
    <div className='signin-page'>
      <div className='signin-bg-pattern'>
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-tl'
        />
        <img
          src={toAbsoluteUrl('/media/logos/ob_logo.svg')}
          alt=''
          aria-hidden='true'
          className='signin-bg-logo signin-bg-logo-br'
        />
      </div>
      <div className='signin-content'>
        <div className='signin-logo'>
          <img
            alt='OmicsBox Web'
            src={toAbsoluteUrl('/media/logos/ob_web_logo.svg')}
            className='signin-logo-img'
          />
        </div>
        <div className='signin-card' data-testid='share-invitation-card' data-state={view.kind}>
          <InvitationContent
            view={view}
            token={token}
            signedInEmail={currentUser?.email}
            onAnswer={answer}
            onSwitchAccount={switchAccount}
            onDone={() => navigate('/dashboard')}
            onRetry={() => setAnswerView(null)}
          />
        </div>
      </div>
    </div>
  )
}

const Icon: React.FC<{tone: 'info' | 'success' | 'error'}> = ({tone}) => (
  <div className='signin-icon-wrap'>
    <i
      className={
        tone === 'success'
          ? 'fas fa-circle-check text-success'
          : tone === 'error'
          ? 'fas fa-circle-exclamation text-danger'
          : `${SHARED_FOLDER_ICON} text-primary`
      }
      style={{fontSize: 40}}
      aria-hidden='true'
    />
  </div>
)

const Muted: React.FC<{children: React.ReactNode}> = ({children}) => (
  <p className='signin-muted' style={{textAlign: 'center'}}>
    {children}
  </p>
)

const InvitationContent: React.FC<{
  view: View
  token: string | null
  signedInEmail?: string
  onAnswer: (choice: 'accept' | 'decline') => void
  onSwitchAccount: () => void
  onDone: () => void
  onRetry: () => void
}> = ({view, token, signedInEmail, onAnswer, onSwitchAccount, onDone, onRetry}) => {
  const doneButton = (
    <button type='button' className='signin-btn signin-btn-secondary' onClick={onDone}>
      Go to Dashboard
    </button>
  )

  switch (view.kind) {
    case 'no_token':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Invitation link incomplete</h1>
          <Muted>
            This link is missing its invitation code. Open the link in your email again.
          </Muted>
        </>
      )
    case 'signed_out':
      return (
        <>
          <Icon tone='info' />
          <h1 className='signin-title'>Shared folder invitation</h1>
          <Muted>
            Someone invited you to a shared folder in OmicsBox. Sign in, or create an account with
            the email address the invitation was sent to, to see it.
          </Muted>
          <div className='signin-invitation-actions'>
            <Link
              to='/signin'
              className='signin-btn signin-btn-primary'
              style={{textDecoration: 'none'}}
              onClick={() => saveReturnUrl(token)}
            >
              Sign In
            </Link>
            <Link
              to='/signin/registration'
              className='signin-btn signin-btn-secondary'
              style={{textDecoration: 'none'}}
              onClick={() => saveReturnUrl(token)}
            >
              Create Account
            </Link>
          </div>
        </>
      )
    case 'loading':
      return (
        <>
          <div className='signin-icon-wrap'>
            <div className='signin-spinner' />
          </div>
          <h1 className='signin-title'>Loading invitation…</h1>
        </>
      )
    case 'ready':
    case 'answering': {
      const {preview} = view
      const busy = view.kind === 'answering'
      return (
        <>
          <Icon tone='info' />
          <h1 className='signin-title text-break'>“{preview.shareName}”</h1>
          <Muted>
            <strong>{preview.ownerName || 'Someone'}</strong> invited you to this shared folder with{' '}
            <strong>{accessLabel(preview.access)}</strong> access.
            <br />
            The invitation expires on {formatShareDate(preview.expiresAt)}.
          </Muted>
          <div className='signin-invitation-actions'>
            <button
              type='button'
              className='signin-btn signin-btn-secondary'
              onClick={() => onAnswer('decline')}
              disabled={busy}
            >
              {busy && view.answer === 'decline' ? 'Declining…' : 'Decline'}
            </button>
            <button
              type='button'
              className='signin-btn signin-btn-primary'
              onClick={() => onAnswer('accept')}
              disabled={busy}
            >
              {busy && view.answer === 'accept' ? 'Accepting…' : 'Accept'}
            </button>
          </div>
        </>
      )
    }
    case 'accepted':
      return (
        <>
          <Icon tone='success' />
          <h1 className='signin-title'>You have access</h1>
          <Muted>
            “{view.share.name}” is now in <strong>Shared</strong> in your Files, with{' '}
            {accessLabel(view.share.access)} access. <OwnedBy ownerName={view.share.ownerName} />.
          </Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'already_member':
      return (
        <>
          <Icon tone='success' />
          <h1 className='signin-title'>You're already a member</h1>
          <Muted>“{view.shareName}” is already in Shared in your Files.</Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'declined':
      return (
        <>
          <Icon tone='info' />
          <h1 className='signin-title'>Invitation declined</h1>
          <Muted>You won't get access to “{view.shareName}”. The owner can invite you again.</Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'wrong_account':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Sign in with the invited address</h1>
          <Muted>
            {view.invitedEmail ? (
              <>
                This invitation was sent to <strong>{view.invitedEmail}</strong>
                {signedInEmail ? (
                  <>
                    , but you're signed in as <strong>{signedInEmail}</strong>
                  </>
                ) : null}
                . Sign in with that address to accept it.
              </>
            ) : (
              view.message
            )}
          </Muted>
          {view.invitedEmail && maskEmail(signedInEmail) === view.invitedEmail && (
            <Muted>
              If that is your address, it may not be verified yet. Verify your email, then open the
              link again.
            </Muted>
          )}
          <div className='signin-invitation-actions'>
            {doneButton}
            <button
              type='button'
              className='signin-btn signin-btn-primary'
              onClick={onSwitchAccount}
            >
              Sign in with another account
            </button>
          </div>
        </>
      )
    case 'used':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Invitation already used</h1>
          <Muted>
            This invitation has already been accepted. If that was you, the folder is in Shared in
            your Files.
          </Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'expired':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Invitation expired</h1>
          <Muted>Invitations last 7 days. Ask the owner of the folder to invite you again.</Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'withdrawn':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Invitation no longer valid</h1>
          <Muted>
            The owner withdrew this invitation, or it was already answered. Ask them to invite you
            again if you still need access.
          </Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'unavailable':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Invitation not available</h1>
          <Muted>
            We couldn't find this invitation. The folder may no longer be shared, or shared folders
            may not be available for your account yet.
          </Muted>
          <div className='signin-invitation-actions'>{doneButton}</div>
        </>
      )
    case 'error':
      return (
        <>
          <Icon tone='error' />
          <h1 className='signin-title'>Couldn't answer the invitation</h1>
          <Muted>{view.message}</Muted>
          <div className='signin-invitation-actions'>
            {doneButton}
            {view.preview && (
              <button type='button' className='signin-btn signin-btn-primary' onClick={onRetry}>
                Try again
              </button>
            )}
          </div>
        </>
      )
  }
}
